import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { StartSessionStep } from "./startSessionStep";
import { InitialRestitutionReviewStep } from "./initialRestitutionReviewStep";
import { RestitutionCorrectionStep } from "./restitutionCorrectionStep";
import { RestitutionCorrectionSummaryStep } from "./restitutionCorrectionSummaryStep";
import { RestitutionValidationStep } from "./restitutionValidationStep";
import { CreateInitialRestitutionForLastSessionStep } from "./createInitialRestitutionForLastSessionStep";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { ErrorPage } from "../../components/ErrorPage";
import { menuSlice } from "../../qrCode/menuSlice";
import { selectDriver, selectLastSession } from "../../qrCode/driverKnownSlice";
import { selectLatestTelemetryMileage } from "../../qrCode/vehicleLoginSlice";
import {
  createCorrectiveVehicleRestitutionAsync,
  getInitialVehicleRestitutionBySessionAsync,
  getVehicleRestitutionPicturesAsync,
  selectCreateCorrectiveVehicleRestitutionStatus,
  selectCreateInitialVehicleRestitutionStatus,
  selectGetInitialVehicleRestitutionBySessionStatus,
  selectGetVehicleRestitutionPicturesStatus,
  selectInitialVehicleRestitution,
  selectValidateVehicleRestitutionStatus,
  selectVehicleRestitutionPictures,
  vehicleSessionsSlice,
} from "../vehicleRestitution/vehicleSessionsSlice";

type BeforeDepartureStep =
  | "startSession"
  | "initialRestitutionCheck"
  | "createInitialRestitution"
  | "correction"
  | "correctionSummary"
  | "validation";

export const VehicleBeforeDeparturePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState<BeforeDepartureStep>("startSession");

  const driver = useAppSelector(selectDriver);
  const lastSession = useAppSelector(selectLastSession);
  const getInitialRestitutionStatus = useAppSelector(
    selectGetInitialVehicleRestitutionBySessionStatus,
  );
  const getRestitutionPictureStatus = useAppSelector(
    selectGetVehicleRestitutionPicturesStatus,
  );
  const initialRestitution = useAppSelector(selectInitialVehicleRestitution);
  const restitutionPictures = useAppSelector(selectVehicleRestitutionPictures);
  const validateStatus = useAppSelector(selectValidateVehicleRestitutionStatus);
  const createCorrectiveStatus = useAppSelector(
    selectCreateCorrectiveVehicleRestitutionStatus,
  );
  const createInitialStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );

  const latestTelemetryMileage = useAppSelector(selectLatestTelemetryMileage);

  const [mileage, setMileage] = useState(0);
  const [isCabinCleaned, setIsCabinCleaned] = useState(false);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(false);
  const [observations, setObservations] = useState("");
  const [oldPictures, setOldPictures] = useState<
    { uuid: string; url: string }[]
  >([]);
  const [newPictures, setNewPictures] = useState<File[]>([]);

  useEffect(() => {
    if (lastSession && getInitialRestitutionStatus === "idle") {
      void dispatch(
        getInitialVehicleRestitutionBySessionAsync({
          driversHistoryUuid: lastSession.uuid,
        }),
      );
    }
  }, [dispatch, lastSession, getInitialRestitutionStatus]);

  useEffect(() => {
    if (
      getInitialRestitutionStatus === "success" &&
      initialRestitution &&
      getRestitutionPictureStatus === "idle"
    ) {
      void dispatch(
        getVehicleRestitutionPicturesAsync({
          restitution: initialRestitution.uuid,
        }),
      );
    }
  }, [
    dispatch,
    getInitialRestitutionStatus,
    initialRestitution,
    getRestitutionPictureStatus,
  ]);

  useEffect(() => {
    if (getInitialRestitutionStatus === "success" && initialRestitution) {
      setMileage(initialRestitution.mileage);
      setIsCabinCleaned(initialRestitution.isCabinCleaned);
      setIsTrunkCleaned(initialRestitution.isTrunkCleaned);
      setObservations(initialRestitution.observations ?? "");
    }
  }, [getInitialRestitutionStatus, initialRestitution]);

  useEffect(() => {
    if (getRestitutionPictureStatus === "success" && restitutionPictures) {
      setOldPictures(restitutionPictures);
    }
  }, [getRestitutionPictureStatus, restitutionPictures]);

  useEffect(() => {
    // The menu reads the success status to raise the toast, then resets it.
    if (validateStatus === "success") navigate("/menu");
  }, [validateStatus, navigate]);

  useEffect(() => {
    if (createCorrectiveStatus === "success") navigate("/menu");
    if (createCorrectiveStatus === "failed") {
      triggerToastDS(t("restitution.correction.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateCorrectiveVehicleRestitutionStatus(),
      );
    }
  }, [createCorrectiveStatus, dispatch, navigate, t]);

  useEffect(() => {
    if (createInitialStatus === "success") {
      setStep("validation");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
  }, [createInitialStatus, dispatch]);

  const onSubmitStartSession = useCallback(
    (date: Date) => {
      const start = new Date(date);
      start.setSeconds(0, 0);
      dispatch(menuSlice.actions.setSessionStartOfUsage(start.toISOString()));
      if (
        initialRestitution?.isValidated ||
        initialRestitution?.isCorrected ||
        lastSession?.driverUuid === driver
      )
        return navigate("/menu");
      setStep(
        initialRestitution
          ? "initialRestitutionCheck"
          : "createInitialRestitution",
      );
    },
    [dispatch, initialRestitution, lastSession, driver, navigate],
  );

  const onCorrect = useCallback(() => {
    setStep("correction");
  }, []);

  const hasTelemetryMileage = useMemo(
    () => latestTelemetryMileage !== null && latestTelemetryMileage > 0,
    [latestTelemetryMileage],
  );
  const displayedMileage = useMemo(
    () => (hasTelemetryMileage ? Number(latestTelemetryMileage) : mileage),
    [hasTelemetryMileage, latestTelemetryMileage, mileage],
  );

  const submitCorrectiveRestitution = useCallback(async () => {
    if (!driver || !initialRestitution) return;
    const picturesToUpload = await Promise.all(
      newPictures.map(async (file) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );
    void dispatch(
      createCorrectiveVehicleRestitutionAsync({
        driverUuid: driver,
        mileage: displayedMileage,
        isCabinCleaned,
        isTrunkCleaned,
        observations: observations === "" ? null : observations,
        initialRestitution: initialRestitution.uuid,
        initialPicturesToKeep: oldPictures.map((picture) => picture.uuid),
        picturesToUpload,
      }),
    );
  }, [
    dispatch,
    driver,
    initialRestitution,
    displayedMileage,
    isCabinCleaned,
    isTrunkCleaned,
    observations,
    oldPictures,
    newPictures,
  ]);

  // the page is only reachable through the QR code flow, which loads the last session
  if (!lastSession) return <ErrorPage page="qrCode" />;

  switch (step) {
    case "startSession":
      return <StartSessionStep onSubmit={onSubmitStartSession} />;
    case "initialRestitutionCheck":
      return <InitialRestitutionReviewStep onCorrect={onCorrect} />;
    case "createInitialRestitution":
      return (
        <CreateInitialRestitutionForLastSessionStep
          mileage={displayedMileage}
          setMileage={setMileage}
          hasTelemetryMileage={hasTelemetryMileage}
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          photos={newPictures}
          setPhotos={setNewPictures}
          onReturn={() => setStep("startSession")}
        />
      );
    case "correction":
      return (
        <RestitutionCorrectionStep
          mileage={String(displayedMileage)}
          setMileage={(value) => setMileage(value === "" ? 0 : Number(value))}
          hasTelemetryMileage={hasTelemetryMileage}
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          uploadedPictures={oldPictures}
          setOldPictures={setOldPictures}
          newPhotos={newPictures}
          setNewPhotos={setNewPictures}
          onReturn={() => setStep("initialRestitutionCheck")}
          onContinue={() => setStep("correctionSummary")}
        />
      );
    case "correctionSummary":
      if (createCorrectiveStatus === "processing") return <DriverLoadingPage />;
      return (
        <RestitutionCorrectionSummaryStep
          mileage={displayedMileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
          oldPictures={oldPictures}
          newPictures={newPictures}
          onReturn={() => setStep("correction")}
          onValidate={() => void submitCorrectiveRestitution()}
          onEdit={() => setStep("correction")}
        />
      );
    case "validation":
      return (
        <RestitutionValidationStep
          mileage={displayedMileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
          oldPictures={oldPictures}
          newPictures={newPictures}
        />
      );
  }
};
